// src/pages/TermsPage.jsx

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const TermsPage = () => {
  // Lütfen bu değişkenleri kendi bilgilerinizle güncelleyin
  const lastUpdated = "September 1, 2024"; // Buraya güncel tarihi girin
  const appName = "Part-66 Exam Pro"; // Buraya uygulamanızın adını girin
  const supportEmail = "support@tabbythecat.com"; // Buraya destek e-posta adresinizi girin
  const companyName = "Tabby Cat, LLC"; // Buraya şirketinizin veya geliştirici adınızı girin
  const country = "Malta"; // Örn: "Turkey" veya "the United States"
  const city = "Luqa"; // Örn: "Istanbul" veya "New York"

  return (
    <section>
      <div className="text-foreground min-h-screen pt-20">
        <div className="container mx-auto max-w-4xl p-4 py-8 md:p-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl md:text-4xl font-bold tracking-tight">
                Terms & Conditions
              </CardTitle>
              <CardDescription className="pt-2">
                Last Updated: {lastUpdated}
              </CardDescription>
            </CardHeader>
            <Separator className="mb-6" />
            <CardContent className="space-y-6 text-muted-foreground leading-7">
              <p>
                PLEASE READ THESE TERMS AND CONDITIONS ("TERMS") CAREFULLY
                BEFORE USING THE {appName} MOBILE APPLICATION (THE
                "APPLICATION").
              </p>
              <p>
                By downloading, installing, accessing, or using the Application,
                You ("You," "User") agree to be bound by these Terms. If You do
                not agree to these Terms, do not use the Application and delete
                it from your device. These Terms constitute a legally binding
                agreement between you and {companyName} ("Licensor," "We,"
                "Us").
              </p>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  1. License Grant
                </h2>
                <p>
                  The Licensor grants You a limited, non-exclusive,
                  non-transferable, revocable license to use the Application for
                  your personal, non-commercial use on any mobile device that
                  You own or control, strictly in accordance with these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  2. Intellectual Property & Usage Restrictions
                </h2>
                <p>
                  All materials within the Application, including but not
                  limited to the text, graphics, user interfaces, logos, and
                  most importantly, the entire database of questions, answers,
                  explanations, and all educational content ("Content"), are the
                  exclusive intellectual property of the Licensor and are
                  protected by copyright and other intellectual property laws.
                </p>
                <p className="font-semibold text-foreground mt-4">
                  The following actions are STRICTLY PROHIBITED and constitute a
                  material breach of this agreement:
                </p>
                <ul className="list-disc pl-6 space-y-3 mt-4">
                  <li>
                    <strong className="text-foreground">
                      No Copying or Distribution:
                    </strong>{" "}
                    You may not copy, reproduce, distribute, republish,
                    download, display, post, or transmit any part of the Content
                    in any form or by any means.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      No Screenshots or Recording:
                    </strong>{" "}
                    You are expressly forbidden from taking screenshots, screen
                    recordings, photographs, or any other form of digital or
                    physical capture of the Application's Content.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      No External Sharing:
                    </strong>{" "}
                    You may not share, leak, or otherwise make the Content
                    available on any other platform, including websites, forums,
                    social media, or any private groups.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      No Reverse Engineering:
                    </strong>{" "}
                    You may not reverse engineer, decompile, disassemble, or
                    otherwise attempt to derive the source code of the
                    Application.
                  </li>
                </ul>
                <p className="mt-4">
                  Violation of these restrictions will result in the immediate
                  and automatic termination of your license. The Licensor
                  reserves the full right to pursue legal action for any
                  infringement of its intellectual property rights.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  3. Disclaimer and Educational Purpose
                </h2>
                <p>
                  This Application is designed as a supplementary study aid for
                  individuals preparing for the European Union Aviation Safety
                  Agency (EASA) Part-66 aircraft maintenance license
                  examinations. You expressly acknowledge and agree to the
                  following:
                </p>
                <ul className="list-disc pl-6 space-y-3 mt-4">
                  <li>
                    <strong className="text-foreground">
                      For Supplementary Use Only:
                    </strong>{" "}
                    The Application is intended for practice and informational
                    purposes ONLY. It is NOT a substitute for official EASA
                    regulations, approved course materials, or instruction from
                    certified institutions.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      Not an Official Question Bank:
                    </strong>{" "}
                    The question bank is for training purposes and does NOT
                    claim to be an official, current, or exhaustive set of
                    questions from actual EASA examinations.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      User Responsibility:
                    </strong>{" "}
                    The ultimate responsibility for passing any examination lies
                    entirely with You. You are solely responsible for your own
                    learning and for consulting official EASA source materials.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  4. No Guarantee of Success
                </h2>
                <p>
                  Use of this Application does not, in any way, guarantee that
                  You will pass any EASA Part-66 examination. Success is
                  dependent on individual effort and a comprehensive
                  understanding of the official syllabus.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  5. Accuracy of Content
                </h2>
                <p>
                  While we strive for accuracy, the Licensor makes no warranties
                  as to the completeness or currency of the Content. Aviation
                  regulations are subject to change. We disclaim all liability
                  for any errors or omissions. Always refer to official EASA
                  publications.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  6. Limitation of Liability
                </h2>
                <p>
                  TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL THE
                  LICENSOR BE LIABLE FOR ANY DAMAGES WHATSOEVER (INCLUDING, BUT
                  NOT LIMITED TO, DAMAGES FOR FAILING AN EXAMINATION, LOSS OF
                  PROFITS, OR DATA) ARISING FROM YOUR USE OF THE APPLICATION.
                  YOUR USE OF THE APPLICATION IS ENTIRELY AT YOUR OWN RISK.
                </p>
                <p className="mt-4">
                  <strong className="text-foreground">Disclaimer:</strong> This
                  application, {appName}, is not affiliated with, endorsed by,
                  or sponsored by EASA. All references to EASA or Part-66 are
                  for identification and educational purposes only.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  7. Governing Law
                </h2>
                <p>
                  This Agreement shall be governed by the laws of {country}. Any
                  dispute arising from these Terms shall be subject to the
                  exclusive jurisdiction of the courts of {city}.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  8. Contact Information
                </h2>
                <p>
                  If you have any questions about these Terms, please contact
                  us:
                  <br />
                  <strong>Support:</strong> {companyName}
                  <br />
                  <strong>Email:</strong>{" "}
                  <a
                    href={`mailto:${supportEmail}`}
                    className="text-primary hover:underline"
                  >
                    {supportEmail}
                  </a>
                </p>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default TermsPage;
